import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { neon } from "@/integrations/neon";
import { Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { FormBuilder } from "./FormBuilder";
import { useContacts, ContactRecord } from "@/hooks/useContacts";
import { validateEmail, sanitizeInput } from "@/utils/security";
import { getErrorMessage } from "@/utils/errors";

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FeedbackForm {
  id: string;
  name: string;
  name_th: string | null;
  description: string | null;
  description_th: string | null;
  created_at?: string;
  user_id?: string;
}

export const SettingsDialog = ({ open, onOpenChange }: SettingsDialogProps) => {
  const [forms, setForms] = useState<FeedbackForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState<FeedbackForm | null>(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const { toast } = useToast();
  const { contacts, isLoading: contactsLoading, refresh: refreshContacts, upsertContact, deleteContact } = useContacts();
  const [contactForm, setContactForm] = useState<{ id?: string; name: string; email: string }>({ name: "", email: "" });
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactDeletingId, setContactDeletingId] = useState<string | null>(null);

  const isSystemForm = (userId?: string) => !userId || userId === '00000000-0000-0000-0000-000000000000';

  const fetchForms = useCallback(async () => {
    try {
      const data = await neon.api.forms.list();
      setForms(data || []);
    } catch (error: unknown) {
      toast({
        title: "Error loading forms",
        description: getErrorMessage(error, "Unable to load forms"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (open) {
      fetchForms();
      refreshContacts();
    }
  }, [open, fetchForms, refreshContacts]);

  const handleCreateForm = () => {
    setSelectedForm(null);
    setShowFormBuilder(true);
  };

  const handleEditForm = (form: FeedbackForm) => {
    setSelectedForm(form);
    setShowFormBuilder(true);
  };

  const handleDeleteForm = async (formId: string) => {
    if (!confirm("Are you sure you want to delete this form? This action cannot be undone.")) {
      return;
    }

    try {
      await neon.api.forms.delete(formId);

      toast({
        title: "Form deleted",
        description: "The feedback form has been deleted successfully.",
      });

      fetchForms();
    } catch (error: unknown) {
      toast({
        title: "Error deleting form",
        description: getErrorMessage(error, "Unable to delete form"),
        variant: "destructive",
      });
    }
  };

  const handleFormSaved = () => {
    setShowFormBuilder(false);
    setSelectedForm(null);
    fetchForms();
  };

  const resetContactForm = () => {
    setContactForm({ name: "", email: "" });
    setIsEditingContact(false);
  };

  const startEditContact = (contact: ContactRecord) => {
    setContactForm({
      id: contact.id,
      name: contact.name ?? "",
      email: contact.email,
    });
    setIsEditingContact(true);
  };

  const handleContactSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedName = sanitizeInput(contactForm.name).trim();
    const trimmedEmail = contactForm.email.trim().toLowerCase();

    if (!validateEmail(trimmedEmail)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setContactSaving(true);

    try {
      await upsertContact({
        id: contactForm.id,
        name: trimmedName || null,
        email: trimmedEmail,
      });

      toast({
        title: isEditingContact ? "Contact updated" : "Contact added",
        description: isEditingContact ? "The contact has been updated." : "A new contact is now available when adding participants.",
      });

      await refreshContacts();
      resetContactForm();
    } catch (error: unknown) {
      toast({
        title: "Unable to save contact",
        description: getErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setContactSaving(false);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!confirm("Delete this contact? They will be removed from participant suggestions.")) {
      return;
    }

    setContactDeletingId(contactId);
    try {
      await deleteContact(contactId);
      toast({
        title: "Contact removed",
        description: "The contact has been deleted.",
      });

      await refreshContacts();
      if (contactForm.id === contactId) {
        resetContactForm();
      }
    } catch (error: unknown) {
      toast({
        title: "Unable to delete contact",
        description: getErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setContactDeletingId(null);
    }
  };

  if (showFormBuilder) {
    return (
      <FormBuilder
        open={showFormBuilder}
        onOpenChange={setShowFormBuilder}
        form={selectedForm}
        onFormSaved={handleFormSaved}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-background/95 backdrop-blur-xl border-border/60 shadow-2xl">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="forms" className="mt-6">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="forms">Feedback Forms</TabsTrigger>
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
          </TabsList>

          <TabsContent value="forms" className="space-y-6 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Feedback Forms</h3>
                <p className="text-sm text-muted-foreground">
                  Create and manage feedback forms for your scripts
                </p>
              </div>
              <Button
                onClick={handleCreateForm}
                className="bg-gradient-primary hover:opacity-90 transition-all duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Form
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : forms.length === 0 ? (
              <Card className="text-center py-12 border-dashed">
                <CardContent>
                  <div className="flex flex-col items-center space-y-4">
                    <AlertTriangle className="h-12 w-12 text-muted-foreground" />
                    <div>
                      <h4 className="font-semibold text-foreground">No forms created</h4>
                      <p className="text-sm text-muted-foreground">
                        Create your first feedback form to start collecting reviews
                      </p>
                    </div>
                    <Button
                      onClick={handleCreateForm}
                      className="bg-gradient-primary hover:opacity-90 transition-all duration-200"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Form
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {forms.map((form) => (
                  <Card key={form.id} className="shadow-sm border-border/60 bg-card/50 hover:bg-card hover:shadow-md transition-all duration-200">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{form.name}</CardTitle>
                          {form.name_th && (
                            <p className="text-sm text-muted-foreground">{form.name_th}</p>
                          )}
                          {form.description && (
                            <CardDescription>{form.description}</CardDescription>
                          )}
                          {form.description_th && (
                            <p className="text-xs text-muted-foreground mt-1">{form.description_th}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {isSystemForm(form.user_id) ? (
                            <div className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full">
                              Default Form
                            </div>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditForm(form)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteForm(form.id)}
                                className="text-destructive hover:text-destructive-foreground hover:bg-destructive"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contacts" className="space-y-6 mt-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Contacts</h3>
              <p className="text-sm text-muted-foreground">
                Manage the people you regularly invite to provide feedback. These contacts appear when assigning participants and sharing links.
              </p>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-4 rounded-lg border border-border/60 p-6 bg-muted/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Name</Label>
                  <Input
                    id="contact-name"
                    value={contactForm.name}
                    onChange={(event) => setContactForm((previous) => ({ ...previous, name: event.target.value }))}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={contactForm.email}
                    onChange={(event) => setContactForm((previous) => ({ ...previous, email: event.target.value }))}
                    placeholder="contact@example.com"
                    required
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" disabled={contactSaving}>
                  {contactSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEditingContact ? "Update contact" : "Add contact"}
                </Button>
                {isEditingContact && (
                  <Button type="button" variant="ghost" onClick={resetContactForm} disabled={contactSaving}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>

            {contactsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : contacts.length === 0 ? (
              <Card className="text-center py-10 border-dashed">
                <CardContent>
                  <p className="text-sm text-muted-foreground">No contacts yet. Add contacts to reuse them when assigning participants.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {contacts.map((contact) => (
                  <div key={contact.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-border/70 bg-background px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{contact.name ?? "Unnamed contact"}</p>
                      <p className="text-xs text-muted-foreground">{contact.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditContact(contact)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteContact(contact.id)}
                        disabled={contactDeletingId === contact.id}
                        className="text-destructive hover:text-destructive-foreground hover:bg-destructive/10"
                      >
                        {contactDeletingId === contact.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
